import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { Input } from '../../components/ui/input';
import { Ban, CheckCircle2, RotateCcw, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const S = {
  h1: { fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', fontWeight: 600, color: '#1F3A2E' },
  select: { padding: '9px 12px', border: '1px solid #D8D1C6', borderRadius: '10px', fontSize: '13px', fontFamily: 'Manrope, sans-serif', background: 'white', cursor: 'pointer', minWidth: '200px' },
  panel: { background: 'white', borderRadius: '14px', border: '1px solid #EFE7DA', padding: '14px 18px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  actionBtn: (bg, color, border) => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', background: bg, color, border: border || 'none', fontSize: '12px', fontWeight: 700, padding: '9px 14px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }),
  legend: { display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6E7F5B' },
  legendBox: (bg) => ({ width: '14px', height: '14px', borderRadius: '3px', background: bg, display: 'inline-block' }),
};

export default function AdminAllotments() {
  const { apiHeaders, language, BACKEND_URL } = useApp();
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [quotaInput, setQuotaInput] = useState('');
  const dragging = useRef(false);
  const dragMode = useRef(true);

  const MONTHS = language === 'tr' ? MONTHS_TR : MONTHS_EN;
  const todayStr = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/rooms`).then(res => {
      setRooms(res.data);
      if (res.data.length > 0) setRoomId(res.data[0]._id);
    }).catch(() => {});
  }, [BACKEND_URL]);

  const fetchData = useCallback(async () => {
    if (!roomId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/allotments/${roomId}?year=${year}`, apiHeaders());
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setLoading(false);
  }, [roomId, year, BACKEND_URL]);

  useEffect(() => { setSelected(new Set()); fetchData(); }, [fetchData]);

  useEffect(() => {
    const up = () => { dragging.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const daysInMonth = (m) => new Date(year, m + 1, 0).getDate();

  const toggleCell = (dateStr, forceAdd = null) => {
    setSelected(prev => {
      const next = new Set(prev);
      const add = forceAdd !== null ? forceAdd : !next.has(dateStr);
      if (add) next.add(dateStr); else next.delete(dateStr);
      return next;
    });
  };

  const onCellDown = (dateStr) => {
    dragging.current = true;
    dragMode.current = !selected.has(dateStr);
    toggleCell(dateStr, dragMode.current);
  };
  const onCellEnter = (dateStr) => {
    if (dragging.current) toggleCell(dateStr, dragMode.current);
  };

  const applyUpdate = async (payload, successMsg) => {
    if (selected.size === 0) {
      toast.error(language === 'tr' ? 'Önce takvimden gün seçin' : 'Select days on the calendar first');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/allotments/${roomId}`, { dates: [...selected], ...payload }, apiHeaders());
      toast.success(successMsg);
      setSelected(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setSaving(false);
  };

  const handleSetQuota = () => {
    const q = parseInt(quotaInput);
    if (isNaN(q) || q < 0) {
      toast.error(language === 'tr' ? 'Geçerli bir kontenjan girin' : 'Enter a valid quota');
      return;
    }
    applyUpdate({ quota: q, stop_sale: false }, language === 'tr' ? `Kontenjan ${q} olarak ayarlandı` : `Quota set to ${q}`);
  };

  const cellInfo = (dateStr) => {
    const ov = data?.overrides?.[dateStr];
    const stopSale = ov?.stop_sale || data?.blocked_dates?.includes(dateStr);
    const quota = ov?.quota != null ? ov.quota : (data?.default_quota ?? 1);
    const booked = data?.booked?.[dateStr] || 0;
    const remaining = Math.max(quota - booked, 0);
    return { stopSale, quota, booked, remaining, hasOverride: !!ov };
  };

  const cellStyle = (dateStr) => {
    const isPast = dateStr < todayStr;
    const isSelected = selected.has(dateStr);
    const { stopSale, remaining } = cellInfo(dateStr);
    let bg = '#4ade80', color = '#14532d';
    if (stopSale) { bg = '#ef4444'; color = 'white'; }
    else if (remaining === 0) { bg = '#f97316'; color = 'white'; }
    if (isPast) { bg = '#e5e7eb'; color = '#9ca3af'; }
    return {
      width: '34px', height: '30px', fontSize: '11px', fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: bg, color,
      border: isSelected ? '2px solid #1F3A2E' : '1px solid rgba(0,0,0,0.08)',
      boxShadow: isSelected ? 'inset 0 0 0 1px #1F3A2E' : 'none',
      cursor: isPast ? 'not-allowed' : 'pointer',
      userSelect: 'none', borderRadius: '4px',
    };
  };

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="admin-allotments-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={S.h1}>{language === 'tr' ? 'Kontenjan Takvimi' : 'Allotment Calendar'}</h1>
          <p style={{ fontSize: '13px', color: '#6E7F5B', marginTop: '2px' }}>
            {language === 'tr'
              ? 'Takvimden gün seçin (sürükleyerek çoklu seçim), sonra kontenjan girin veya satışa kapatın.'
              : 'Select days on the calendar (drag for multi-select), then set quota or stop sale.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <select value={roomId} onChange={e => setRoomId(e.target.value)} style={S.select} data-testid="allotment-room-select">
            {rooms.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ ...S.select, minWidth: '100px' }} data-testid="allotment-year-select">
            {[0, 1, 2].map(offset => {
              const y = new Date().getFullYear() + offset;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </div>
      </div>

      {/* Action panel */}
      <div style={S.panel}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: '#1F3A2E' }} data-testid="allotment-selected-count">
          {selected.size} {language === 'tr' ? 'gün seçili' : 'days selected'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Input
            type="number" min="0" value={quotaInput}
            onChange={e => setQuotaInput(e.target.value)}
            placeholder={language === 'tr' ? 'Kontenjan' : 'Quota'}
            style={{ width: '90px', border: '1px solid #D8D1C6', borderRadius: '9px', fontSize: '13px' }}
            data-testid="allotment-quota-input"
          />
          <button onClick={handleSetQuota} disabled={saving} style={S.actionBtn('#1F3A2E', 'white')} data-testid="allotment-apply-quota-button">
            {saving ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle2 size={13} />}
            {language === 'tr' ? 'Uygula' : 'Apply'}
          </button>
        </div>
        <button onClick={() => applyUpdate({ stop_sale: true }, language === 'tr' ? 'Seçili günler satışa kapatıldı' : 'Selected days closed for sale')} disabled={saving} style={S.actionBtn('#ef4444', 'white')} data-testid="allotment-stop-sale-button">
          <Ban size={13} /> {language === 'tr' ? 'Satışa Kapat (S)' : 'Stop Sale (S)'}
        </button>
        <button onClick={() => applyUpdate({ stop_sale: false }, language === 'tr' ? 'Seçili günler satışa açıldı' : 'Selected days opened for sale')} disabled={saving} style={S.actionBtn('white', '#16a34a', '1px solid #86efac')} data-testid="allotment-open-sale-button">
          <CheckCircle2 size={13} /> {language === 'tr' ? 'Satışa Aç' : 'Open Sale'}
        </button>
        <button onClick={() => applyUpdate({ reset: true }, language === 'tr' ? 'Varsayılana döndürüldü' : 'Reset to default')} disabled={saving} style={S.actionBtn('white', '#6E7F5B', '1px solid #D8D1C6')} data-testid="allotment-reset-button">
          <RotateCcw size={13} /> {language === 'tr' ? 'Varsayılana Dön' : 'Reset to Default'}
        </button>
        {selected.size > 0 && (
          <button onClick={() => setSelected(new Set())} style={S.actionBtn('transparent', '#6E7F5B')} data-testid="allotment-clear-selection-button">
            <X size={13} /> {language === 'tr' ? 'Seçimi Temizle' : 'Clear Selection'}
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          <span style={S.legend}><span style={S.legendBox('#4ade80')} /> {language === 'tr' ? 'Müsait' : 'Available'}</span>
          <span style={S.legend}><span style={S.legendBox('#f97316')} /> {language === 'tr' ? 'Dolu' : 'Full'}</span>
          <span style={S.legend}><span style={S.legendBox('#ef4444')} /> {language === 'tr' ? 'Satışa Kapalı' : 'Stop Sale'}</span>
        </div>
      </div>

      {/* Default quota info */}
      {data && (
        <p style={{ fontSize: '12px', color: '#6E7F5B', marginBottom: '12px' }}>
          {language === 'tr'
            ? `Bu odanın varsayılan kontenjanı: ${data.default_quota} adet/gün. (Oda Yönetimi'nden değiştirilebilir. Hücrelerde kalan müsait adet gösterilir.)`
            : `Default quota for this room: ${data.default_quota} units/day. (Editable via Room Management. Cells show remaining availability.)`}
        </p>
      )}

      {/* Calendar grid */}
      <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #EFE7DA', padding: '16px', overflowX: 'auto' }}>
        {loading ? (
          <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6E7F5B' }}>
            {language === 'tr' ? 'Yükleniyor...' : 'Loading...'}
          </div>
        ) : (
          <table style={{ borderCollapse: 'separate', borderSpacing: '2px' }}>
            <thead>
              <tr>
                <th style={{ minWidth: '86px' }} />
                {Array.from({ length: 31 }, (_, i) => (
                  <th key={i} style={{ fontSize: '10px', color: '#6E7F5B', fontWeight: 700, padding: '2px' }}>{i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MONTHS.map((mName, m) => (
                <tr key={m}>
                  <td style={{ fontSize: '12px', fontWeight: 700, color: '#1F3A2E', paddingRight: '8px', whiteSpace: 'nowrap' }}>
                    {mName}/{year}
                  </td>
                  {Array.from({ length: 31 }, (_, d) => {
                    const day = d + 1;
                    if (day > daysInMonth(m)) return <td key={d} />;
                    const dateStr = `${year}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const isPast = dateStr < todayStr;
                    const { stopSale, remaining } = cellInfo(dateStr);
                    return (
                      <td key={d} style={{ padding: 0 }}>
                        <div
                          style={cellStyle(dateStr)}
                          onMouseDown={() => !isPast && onCellDown(dateStr)}
                          onMouseEnter={() => !isPast && onCellEnter(dateStr)}
                          title={dateStr}
                          data-testid={`allotment-cell-${dateStr}`}
                        >
                          {stopSale ? 'S' : remaining}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
